import { type GenericMap, type I18n } from "@client/types";
import { type HttpResponse } from "@client/types/Api";
import appInfo from "@common/appInfo";
import i18n, { localize } from "@common/lib/i18n";
import { parseQueryString, updateQueryString } from "@common/lib/url";
import { isDate, omit } from "lodash";

export interface RequestBaseArgs {
  method: string;
  url: string;
  query?: GenericMap<any>;
  body?: GenericMap<any>;
  headers?: object;
  cache?: CacheType;
  i18n?: I18n;
}

export const download = async (args: RequestBaseArgs, name?: string): Promise<void> => {
  const response = await _requestBase(args);
  if (response.status >= 300) return; // TODO: better handling to avoid breaking the UI. throw new Error(response.statusText);

  const blob = await response.blob();

  //Get the name of the downloaded file from the header
  const match = (response.headers.get("Content-Disposition") || "").match(/filename=([^;]+)/);
  const fileName = name ?? ((match ? match[1] : "") || blob["name"]);

  //Create anchor tag with local file and click it for download, then remove it
  const a = document.createElement("a");
  a.href = window.URL.createObjectURL(blob);
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  a.remove();
};

export const downloadFile = async (url: string, name?: string): Promise<void> =>
  download(
    {
      method: "GET",
      url: url
    },
    name
  );

type CacheType = RequestCache | "memory";
export const fetchJsonFile = async <T>(url: string, cache?: CacheType): Promise<T | null> => {
  const res = await _requestBase({ method: "GET", url, cache });
  if (!res.ok) return null;

  try {
    const json = await res.text();
    return JSON.parse(json) as T;
  } catch {
    return null;
  }
};

// NOTE: we have add current page URL parameters to API request, so parameters like icc (currency code) are handled correctly.
const buildUrl = (url: string, query?: object): string => {
  const pageUrlQuery = parseQueryString();
  const mergedQuery = Object.assign(pageUrlQuery, query ?? {});

  return Object.keys(mergedQuery).reduce((res, key) => addQueryParam(res, key, mergedQuery[key]), url);
};

const _requestBase = (args: RequestBaseArgs): Promise<Response> => {
  args.headers ??= {};

  args.query = {
    ...parseQueryString(args.url),
    ...args.query
  };

  if (args.url.startsWith("/api/site/v1")) {
    addHeader(args, "X-PortalId", "portalId");
    addHeader(args, "X-PageId", "pageId");
    addHeader(args, "X-PageVariantId", "pageVariantId");
    addHeader(args, "X-Path", "path");
    addHeader(args, "X-ModuleId", "moduleId");
    addHeader(args, "X-PopupId", "popupId");
    addHeader(args, "X-CultureCode", "cultureCode");

    args.headers["X-CultureCode"] ??= appInfo.cultureCode;
  }

  const url = updateQueryString(args.url, args.query);

  return fetch(url, {
    method: args.method,
    headers: {
      ...(args.body ? { "Content-Type": "application/json" } : {}),
      ...(args.headers || {})
    },
    mode: "cors",
    credentials: !url.startsWith("http") || url.includes(window.location.host) ? "include" : "omit",
    body: args.body ? JSON.stringify(args.body) : undefined // body data type must match "Content-Type" header
  });
};

export const addQueryParam = (url: string, key: string, value: any) =>
  (value || value === 0 || value === "" || value === false) && !(Array.isArray(value) && !value.length)
    ? `${url}${url.indexOf("?") >= 0 ? "&" : "?"}${encodeURI(`${key}=${isDate(value) ? value.toISOString() : value}`)}`
    : url;
export type RequestArgs = RequestBaseArgs;

let requestPromises: GenericMap<Promise<any>> = {};
const getCacheKey = (args: RequestBaseArgs): string =>
  buildUrl(args.url, omit(args.query, "context", "portalId", "pageVariantId", "screenType", "moduleId", "path")); // remove module specific parameters

const addHeader = (args: RequestArgs, name: string, prop: string) => {
  args.headers ??= {};
  args.headers[name] = args.body?.[prop] ?? args.query?.[prop];

  if (!args.headers[name]) delete args.headers[name];

  delete args.query?.[prop];
};

export const resetCache = () => {
  requestPromises = {};
};

export const request = async <T extends object>(args: RequestArgs): Promise<HttpResponse<T>> => {
  if (args.cache && args.method === "GET") {
    // only GET requests can be cached
    const cacheKey = getCacheKey(args);
    if (!(cacheKey in requestPromises)) {
      requestPromises[cacheKey] = requestBase(args);
    }
    return requestPromises[cacheKey];
  }

  return requestBase(args);
};
export const requestBase = <T>(args: RequestBaseArgs): Promise<HttpResponse<T>> => {
  return _requestBase(args)
    .then((response) => {
      if (!response.ok) {
        return response.text().then((text) => {
          let error;
          let parsed;
          try {
            parsed = JSON.parse(text);
            error = parsed.error;
          } catch (e) {}

          return <HttpResponse<T>>{
            status: response.status,
            error: error || response.statusText,
            response: parsed,
            formFieldErrors: parsed.formFieldErrors
          };
        });
      }
      if (response.headers.get("Content-Type")?.includes("application/json")) {
        return response.json().then(
          (data): HttpResponse<T> => ({
            status: response.status,
            response: data as T,
            redirectUrl: response.redirected ? response.url : null
          })
        );
      }
      return response.text().then(
        (data): HttpResponse<T> => ({
          status: response.status,
          response: data as unknown as T,
          redirectUrl: response.redirected ? response.url : null
        })
      );
    })
    .then((res) => {
      // TODO: we shouldn't try localize NEXT content. HACK: skip settings only
      if (res.response && typeof res.response === "object") {
        const i18nInternal = args.i18n ?? i18n;
        localize(i18nInternal, res.response as any, (x) => x === "settings");

        if (res.response["error"]) {
          res.response["error"] = i18nInternal[res.response["error"]];
        }
      }

      return res;
    })
    .catch((error) => ({ status: 500, error, redirectUrl: null }));
};

export interface UploadArgs {
  method: string;
  files: FileList;
  url: string;
  onProgress?: (percent: number) => void;
}
export const upload = (opts: UploadArgs): Promise<XMLHttpRequest> => {
  return new Promise((res, rej) => {
    var xhr = new XMLHttpRequest();

    xhr.onload = () => res(xhr);
    xhr.onerror = rej;
    if (xhr.upload && opts.onProgress) {
      xhr.upload.onprogress = (event: any) => opts.onProgress!(Math.round((event.loaded / event.total) * 100)); //  ; //event.lengthComputable
    }

    const formData = new FormData();
    for (let i = 0; i < opts.files.length; i++) {
      formData.append("files", opts.files[i]);
    }

    xhr.open(opts.method, opts.url);
    xhr.send(formData);
  });
};
